import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiServiceHelper from "../../auth/apiServiceHelper";

export const fetchCategories = createAsyncThunk(
  "category/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiServiceHelper.getDataOverCategory(
        "shop/over_categories"
      );
      return response.data.over_categories.sort(
        (a, b) => a.order_by - b.order_by
      );
    } catch (err) {
      // Keep the HTTP status: a 404 here means "this laundry has no active
      // product for the shop" (the backend hides fully-unavailable
      // over-categories), which is an empty catalogue, not a failure.
      return rejectWithValue({
        status: err?.response?.status ?? null,
        error: err?.response?.data?.error ?? err?.message ?? null,
      });
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "category/fetchProducts",
  async (categoryId) => {
    const response = await ApiServiceHelper.getDataProductsWithParams(
      `shop/products?category_id=${categoryId}`
    );
    return response.data.products;
  }
);

// The chip row hides `inactive` categories (the shop/over_categories endpoint
// only filters the OVER-categories by status, not the categories underneath),
// and it sorts them by order_by. Anything that picks a default subcategory has
// to use the exact same rule, otherwise the selected id is one the operator
// cannot see: no chip highlighted, no products, an empty screen.
const visibleCategories = (overCategory) =>
  (overCategory?.categories ?? [])
    .filter((category) => category?.status !== "inactive")
    .sort((a, b) => (a?.order_by || 0) - (b?.order_by || 0));

const firstVisibleCategoryId = (overCategory) =>
  visibleCategories(overCategory)[0]?.id ?? null;

const showsCategory = (overCategory, categoryId) =>
  categoryId != null &&
  visibleCategories(overCategory).some((category) => category.id === categoryId);

const initialStateValues = {
  printerSettings: {
    printerIP: null,
    printerPort: null,
  },
  activeButton: "new",
  count: 0,
  items: [],
  categoryItems: [],
  selectedCategoryIndex: 0,
  selectedSubcategoryId: null,
  products: [],
  selectedItems: [],
  totalAmount: 0,
  totalPerAmount: 0,
  userInfo: {
    description: "",
    payUponDelivery: true,
    printTicket: true,
    emailTicket: false,
    shopDeliveryFee: null,
  },
  bags: [],
  totalBag: 0,
  error: null,
  // true once the backend told us the laundry has no active product for the
  // shop (404 on over_categories, or an empty list). Drives the friendly
  // empty-catalogue state on Home instead of an error.
  catalogueEmpty: false,
};

const cartSlice = createSlice({
  name: "products",
  initialState: initialStateValues,
  reducers: {
    setActiveButton: (state, action) => {
      state.activeButton = action.payload;
    },
    setSelectedCategoryIndex: (state, action) => {
      state.selectedCategoryIndex = action.payload;
      // Re-derive the subcategory whenever the current one is not part of the
      // over-category being shown — including when the index did not change.
      // That same-index case is what left the operator stuck: an over-category
      // displayed with no chip selected and no way to repair it by clicking.
      const selectedCategory = state.categoryItems[action.payload];
      if (!showsCategory(selectedCategory, state.selectedSubcategoryId)) {
        state.selectedSubcategoryId = firstVisibleCategoryId(selectedCategory);
      }
    },
    setSelectedSubcategoryId: (state, action) => {
      state.selectedSubcategoryId = action.payload;
    },
    addItem(state, action) {
      const id = action.payload;
      const existingProduct = state.selectedItems.find(
        (item) => item.id === id
      );
      if (existingProduct) {
        const index = state.selectedItems.findIndex((item) => item.id === id);
        state.selectedItems[index].quantity = existingProduct.quantity + 1;
      } else {
        const product = state.products.find((product) => product.id === id);
        if (product) {
          const newItem = {
            id: product.id,
            quantity: 1,
            name: product.name,
            name_fr: product.name_fr,
            name_nl: product.name_nl,
            price: product.price,
          };
          state.selectedItems.push(newItem);
        } else {
          console.error(`Product with id ${id} not found.`);
        }
      }
      state.totalAmount = 0;
      state.selectedItems.forEach((item) => {
        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity;
        state.totalAmount += item.price * quantity;
      });
    },    
    removeItem(state, action) {
      const id = action.payload;
      const existingProductIndex = state.selectedItems.findIndex(
        (item) => item.id === id
      );

      if (existingProductIndex !== -1) {
        const item = state.selectedItems[existingProductIndex];
        // const isKilo = item.name === 'Per kilo laundry' || item.name === 'Ironing per kilo';
        const currentQuantity = item.quantity;
        
        if (currentQuantity > 1) {
          state.selectedItems[existingProductIndex].quantity = currentQuantity - 1;
        } else {
          state.selectedItems.splice(existingProductIndex, 1);
        }
      } else {
        console.error(`Product with id ${id} not found in selected items.`);
      }
      state.totalAmount = 0;
      state.selectedItems.forEach((item) => {
        const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity;
        state.totalAmount += item.price * quantity;
      });
    },
    updateItemQuantity: {
      reducer(state, action) {
        const { id, quantity } = action.payload.id;
        const index = state.selectedItems.findIndex(item => item.id === id);
        
        if (index !== -1) {
          // Validate the input based on product type
          const item = state.selectedItems[index];
          const isKilo = item.name === 'Per kilo laundry' || item.name === 'Ironing per kilo';
          
          if (isKilo) {
            // For kilo items, validate decimal
            if (/^\d*\.?\d*$/.test(quantity) && quantity !== '' && quantity !== '.') {
              const numValue = parseFloat(quantity);
              state.selectedItems[index].quantity = numValue >= 0.1 ? quantity : '0.1';
            }
          } else {
            // For non-kilo items, validate integer
            if (/^\d*$/.test(quantity)) {
              const numValue = parseInt(quantity);
              state.selectedItems[index].quantity = numValue >= 1 ? numValue : 1;
            }
          }
        }
        state.totalAmount = 0;
        state.selectedItems.forEach((item) => {
          const quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity;
          state.totalAmount += item.price * quantity;
        });
      },
      prepare(id, quantity) {
        return { payload: { id, quantity } };
      }
    },
    addBag(state, action) {
      const bag_id = action.payload;
      const existingBag = state.bags.find((bag) => bag.id === bag_id);
      if (existingBag) {
        existingBag.driver_quantity += 1;
      }
    },
    removeBag(state, action) {
      const bag_id = action.payload;
      const existingBag = state.bags.find((bag) => bag.id === bag_id);
      if (existingBag && existingBag.driver_quantity > 0) {
        existingBag.driver_quantity -= 1;
      }
    },
    setSelectedItemsClear: (state, action) => {
      state.selectedItems = [];
      state.totalAmount = 0;
      state.userInfo.description = "";
      state.userInfo.pickable_id = "";
      state.userInfo.pickable_type = "";
      state.userInfo.deliverable_id = "";
      state.userInfo.deliverable_type = "";
      state.userInfo.selectedShop = "";
      state.userInfo.selectedAddress = "";
      state.userInfo.selectedPoint = "";
      state.userInfo.selectedShopDeliverable = "";
      state.userInfo.selectedAddressDeliverable = "";
      state.userInfo.selectedPointDeliverable = "";
      state.userInfo.pickup_date = "";
      state.userInfo.delivery_date = "";
      state.userInfo.emailTicket = "";
    },
    setShopId: (state, action) => {
      state.userInfo.shopId = action.payload;
    },
    setShopName: (state, action) => {
      state.userInfo.shopName = action.payload;
    },
    setShopDeliveryFee: (state, action) => {
      state.userInfo.shopDeliveryFee = action.payload;
    },
    setLaundryPlaceId: (state, action) => {
      state.userInfo.laundryPlaceId = action.payload;
    },
    // account_linkage_live from POST /shop_auth/sign_in: whether app
    // invitations (account claim) are switched on. Only an explicit false hides
    // the invite button — a till signed in before the field existed keeps it,
    // and the backend still refuses with account_linkage_off if it is off.
    setAccountLinkageLive: (state, action) => {
      state.userInfo.accountLinkageLive = action.payload;
    },
    // The selected customer's email state from GET shop/users/:id, null while
    // unknown: { functionalEmails, emailUndeliverable, appAccess }. Drives the
    // "Send instruction emails" hints.
    setSelectedUserEmailState: (state, action) => {
      state.userInfo.selectedUserEmailState = action.payload;
    },
    // email_flow_settings from POST /shop_auth/sign_in: which customer-email
    // flows the backend runs for this shop's laundry, e.g.
    // { laundry_shop: false, laundry_delivery: true }. Absent on a session
    // signed in before the backend sent it.
    setEmailFlowSettings: (state, action) => {
      state.userInfo.emailFlowSettings = action.payload;
    },
    setSelectedUserId: (state, action) => {
      state.userInfo.selectedUserId = action.payload;
    },
    setSelectedUserPhone: (state, action) => {
      state.userInfo.selectedUserPhone = action.payload;
    },
    setSelectedUserVat: (state, action) => {
      state.userInfo.selectedUserVat = action.payload;
    },
    setSelectedUserCompanyName: (state, action) => {
      state.userInfo.selectedUserCompanyName = action.payload;
    },
    setSelectedUserInvoicingAddress: (state, action) => {
      state.userInfo.selectedUserInvoicingAddress = action.payload;
    },
    setSelectedUserEmail: (state, action) => {
      state.userInfo.selectedUserEmail = action.payload;
    },
    setSelectedUserMonthlyInvoice: (state, action) => {
      state.userInfo.selectedUserMonthlyInvoice = action.payload;
    },
    // Clears the per-customer contact/billing fields. MUST be dispatched when
    // a customer is selected: these fields are hydrated lazily, so without
    // the reset the previous customer's VAT/company/email leak into the next
    // one (seen in testing: a PEPPOL invoice issued to the wrong company via
    // a stale VAT).
    resetSelectedUserBilling: (state) => {
      state.userInfo.selectedUserPhone = "";
      state.userInfo.selectedUserVat = "";
      state.userInfo.selectedUserCompanyName = "";
      state.userInfo.selectedUserInvoicingAddress = "";
      state.userInfo.selectedUserEmail = "";
    },
    setPickUp: (state, action) => {
      state.userInfo.pickable_id = action.payload;
    },
    setPickUpType: (state, action) => {
      state.userInfo.pickable_type = action.payload;
    },
    setDeliver: (state, action) => {
      state.userInfo.deliverable_id = action.payload;
    },
    setDeliverType: (state, action) => {
      state.userInfo.deliverable_type = action.payload;
    },
    setSelectedShop: (state, action) => {
      state.userInfo.selectedShop = action.payload;
    },
    setSelectedAddress: (state, action) => {
      state.userInfo.selectedAddress = action.payload;
    },
    setSelectedPoint: (state, action) => {
      state.userInfo.selectedPoint = action.payload;
    },
    setSelectedAddDelivery: (state, action) => {
      state.userInfo.selectedAddDelivery = action.payload;
    },
    setSelectedShopDeliverable: (state, action) => {
      state.userInfo.selectedShopDeliverable = action.payload;
    },
    setSelectedAddressDeliverable: (state, action) => {
      state.userInfo.selectedAddressDeliverable = action.payload;
    },
    setSelectedPointDeliverable: (state, action) => {
      state.userInfo.selectedPointDeliverable = action.payload;
    },
    setPickupDate: (state, action) => {
      state.userInfo.pickup_date = action.payload;
    },
    setDeliveryDate: (state, action) => {
      state.userInfo.delivery_date = action.payload;
    },
    setPickupSlot: (state, action) => {
      state.userInfo.pickup_slot = action.payload;
    },
    setDeliverySlot: (state, action) => {
      state.userInfo.delivery_slot = action.payload;
    },
    setDate: (state, action) => {
      state.userInfo.delivery_slot = action.payload;
    },
    setBags(state, action) {
      state.bags = action.payload.map((bag) => ({
        ...bag,
        bag_id: bag.id,
        driver_quantity: 0,
      }));
    },
    setDescription: (state, action) => {
      state.userInfo.description = action.payload;
    },
    setWoshUserId: (state, action) => {
      state.userInfo.wosh_User_Id = action.payload;
    },
    setUserName: (state, action) => {
      state.userInfo.userName = action.payload;
    },
    setServicefee: (state, action) => {
      state.userInfo.service_fee = action.payload;
    },
    setPaymentType: (state, action) => {
      state.userInfo.paymentType = action.payload;
    },
    setOrderRetrieveData: (state, action) => {
      state.userInfo.orderRetrieveData = action.payload;
    },
    setPayUponDelivery: (state, action) => {
      state.userInfo.payUponDelivery = action.payload;
    },
    setPrintTicket: (state, action) => {
      state.userInfo.printTicket = action.payload;
    },
    setEmailTicket: (state, action) => {
      state.userInfo.emailTicket = action.payload;
    },
    setPrinterPort: (state, action) => {
      if (!state.printerSettings) {
        state.printerSettings = {}; 
      }
      state.printerSettings.printerPort = action.payload;
    },
    setPrinterIP: (state, action) => {
      if (!state.printerSettings) {
        state.printerSettings = {};
      }
      state.printerSettings.printerIP = action.payload;
    },
    clearProducts: (state, action) => {
      state.products = [];
    },
    clearState: (state, action) => {
      state.activeButton = "new";
      state.count = 0;
      state.items = [];
      state.categoryItems = [];
      state.selectedCategoryIndex = 0;
      state.selectedSubcategoryId = null;
      state.products = [];
      state.selectedItems = [];
      state.totalAmount = 0;
      state.totalPerAmount = 0;
      state.userInfo = {};
      state.bags = [];
      state.totalBag = 0;
      state.error = null;
      state.catalogueEmpty = false;
      state.printerSettings = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.fulfilled, (state, action) => {
        if (action.payload.length === 0) {
          // Empty catalogue. Keep the existing [] reference: consumers key
          // effects on the categoryItems identity (YourItems refetches when
          // it changes), so a fresh [] on every fetch would loop.
          if (state.categoryItems.length) state.categoryItems = [];
          state.selectedCategoryIndex = 0;
          state.selectedSubcategoryId = null;
          state.products = [];
          state.catalogueEmpty = true;
          state.error = null;
          return;
        }
        // Home refetches the catalogue on every mount, so the operator can
        // (and does) click an over-category while the request is in flight.
        // Restore their over-category by id rather than trusting the index —
        // the list can come back in a different order or a different size —
        // and validate the subcategory against the catalogue that just landed
        // so a stale id can never survive into the render.
        const previousOverCategoryId =
          state.categoryItems[state.selectedCategoryIndex]?.id;
        state.categoryItems = action.payload;

        const restoredIndex = action.payload.findIndex(
          (overCategory) => overCategory.id === previousOverCategoryId
        );
        const index = restoredIndex === -1 ? 0 : restoredIndex;
        state.selectedCategoryIndex = index;

        if (!showsCategory(action.payload[index], state.selectedSubcategoryId)) {
          state.selectedSubcategoryId = firstVisibleCategoryId(
            action.payload[index]
          );
        }
        state.catalogueEmpty = false;
        state.error = null;
      })
      .addCase(fetchCategories.pending, (state, action) => {
        // A catalogue refresh puts the counter back on the first over-category,
        // as it always has. `fulfilled` re-reads the index from here, so a
        // click that lands while the request is in flight still wins.
        state.selectedCategoryIndex = 0;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        // Only the backend's own JSON 404 ({ error: "No active over
        // categories…" }) means "empty catalogue". An infrastructure 404
        // (HTML body, no `error` string) is a failure like any other, and
        // must not wipe the persisted menu.
        const emptyCatalogue =
          action.payload?.status === 404 &&
          typeof action.payload?.error === "string";
        if (emptyCatalogue) {
          // Drop any persisted categories so a stale menu never lingers —
          // but keep the [] reference stable (see fulfilled) to avoid loops.
          if (state.categoryItems.length) state.categoryItems = [];
          state.selectedSubcategoryId = null;
          state.products = [];
          state.catalogueEmpty = true;
          state.error = null;
          return;
        }
        state.catalogueEmpty = false;
        state.error = "Failed to load categories.";
      })
      .addCase(fetchProducts.pending, (state, action) => {
        state.products = [];
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.error = "Failed to load products.";
      });
  },
});

export const {
  setPrinterIP,
  setPrinterPort,
  setServicefee,
  setWoshUserId,
  setSelectedUserEmail,
  setUserName,
  setSelectedUserMonthlyInvoice,
  resetSelectedUserBilling,
  addItem,
  removeItem,
  updateItemQuantity,
  setSelectedCategoryIndex,
  setSelectedSubcategoryId,
  setSelectedUserId,
  setSelectedUserPhone,
  setSelectedUserVat,
  setSelectedUserCompanyName,
  setSelectedUserInvoicingAddress,
  setPickUp,
  setPickUpType,
  setDeliver,
  setDeliverType,
  setShopId,
  setShopName,
  setShopDeliveryFee,
  setLaundryPlaceId,
  setAccountLinkageLive,
  setSelectedUserEmailState,
  setEmailFlowSettings,
  setSelectedShop,
  setSelectedAddress,
  setSelectedPoint,
  setSelectedShopDeliverable,
  setSelectedAddressDeliverable,
  setSelectedPointDeliverable,
  setRestorePreviousAddress,
  setPickupDate,
  setDeliveryDate,
  setPickupSlot,
  setDeliverySlot,
  newDate,
  addBag,
  removeBag,
  setBags,
  setDescription,
  setSelectedItemsClear,
  setSelectedAddDelivery,
  setPaymentType,
  setOrderRetrieveData,
  setPayUponDelivery,
  setPrintTicket,
  setEmailTicket,
  setActiveButton,
  clearProducts,
  clearState,
} = cartSlice.actions;
export default cartSlice.reducer;
